import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type { OnUpdate } from '@/_schemas/types';
import { resetFormState } from '@/_shared';

import { invalidateContactQuery } from '../apis';
import { contactFormToUpdate, contactQueryToForm, fieldNames } from '../converters';
import type { ContactFormSchema, ContactQuerySchema, ContactUpdateSchema } from '../schemas';

const DATES = ['wedding_anniversary', 'work_anniversary'];

export function useRevisionHistory(
  form: UseFormReturn<ContactFormSchema>,
  onUpdate: OnUpdate<ContactUpdateSchema, { data: ContactQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof ContactFormSchema) => {
    return (
      field === 'contacts_associated_with' ||
      field === 'alternate_clients' ||
      field === 'phone_numbers' ||
      field === 'addresses' ||
      field === 'emails' ||
      field === 'profile_image'
    );
  }, []);

  const onRevert: OnRevert<ContactFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV: ContactUpdateSchema = {} as ContactUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = contactFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = contactFormToUpdate({ ...allFormValues });
        nV = { ...nV, [field]: oldValue };
      }
      const res = await onUpdate(nV, 'Contact changes have been reverted.', true, false);
      if (res.data) {
        if (isPivot) {
          invalidateContactQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<ContactQuerySchema, ContactFormSchema> = useCallback((q) => {
    return contactQueryToForm({ ...q });
  }, []);

  const nameConverter: NameConverter<ContactFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) {
      return null;
    }
    return fieldNames[f].name;
  }, []);

  const valueConverter: ValueConverter<ContactFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];

    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    ) {
      return null;
    }

    if (f === 'interests' && Array.isArray(v)) {
      return v.map((item) => ('name' in item ? item.name : '')).join(', ');
    } else if (typeof v === 'object') {
      if ('name' in v) {
        return v.name;
      }
      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v, DATES.includes(f));
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  const pivotClassifier: PivotClassifier<ContactFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;

      if (isPivot) {
        switch (f) {
          case 'phone_numbers':
          case 'emails':
            changeSetExtractorType = 'PHONE_NUMBER_EMAIL';
            break;
          case 'addresses':
            changeSetExtractorType = 'ADDRESS';
            break;
          case 'contacts_associated_with':
            changeSetExtractorType = 'FIRST_NAME_LAST_NAME';
            break;
          case 'alternate_clients':
            changeSetExtractorType = 'NAMED';
            break;
          case 'profile_image':
            changeSetExtractorType = 'MEDIA';
            break;
        }
      }
      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const pivotValueCalculator: PivotValueCalculator<ContactFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      // !important to avoid audit issues on emails default is required too for editing
      if (validatePivot(field) && field !== 'emails') {
        const nV = contactFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];
        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });

        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
